import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Loader2, Percent, DollarSign } from 'lucide-react';
import { BundleFormDialog } from '@/components/admin/bundles/BundleFormDialog';

import { API } from '@/lib/apiBase';

export default function AdminBundles() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [bundles, setBundles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pricingMode, setPricingMode] = useState('discount'); // 'discount' or 'fixed'
  const [form, setForm] = useState({ name: '', class_id: '', discount_percent: '', fixed_price: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const fetchData = async function() {
    try {
      const headers = getAuthHeaders();
      const [bundlesRes, classesRes] = await Promise.all([
        fetch(API + '/admin/bundles', { headers: headers, credentials: 'include' }),
        fetch(API + '/classes', { headers: headers, credentials: 'include' })
      ]);
      if (bundlesRes.ok) setBundles(await bundlesRes.json());
      if (classesRes.ok) setClasses(await classesRes.json());
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(function() { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: mount-only fetch, no fetchData in deps

  const openCreate = function() {
    setEditing(null);
    setPricingMode('discount');
    setForm({ name: '', class_id: '', discount_percent: '', fixed_price: '', is_active: true });
    setDialogOpen(true);
  };

  const openEdit = function(bundle) {
    setEditing(bundle);
    const hasFixed = bundle.fixed_price != null && bundle.fixed_price > 0;
    setPricingMode(hasFixed ? 'fixed' : 'discount');
    setForm({
      name: bundle.name,
      class_id: bundle.class_id || '',
      discount_percent: String(bundle.discount_percent || ''),
      fixed_price: hasFixed ? String(bundle.fixed_price) : '',
      is_active: bundle.is_active
    });
    setDialogOpen(true);
  };

  const handleSave = async function() {
    if (!form.name) {
      toast.error(t('bundles.fillRequired'));
      return;
    }
    const body = {
      name: form.name,
      class_id: form.class_id || null,
      is_active: form.is_active,
      discount_percent: 0,
      fixed_price: null
    };
    if (pricingMode === 'fixed') {
      const fp = parseFloat(form.fixed_price);
      if (isNaN(fp) || fp <= 0) {
        toast.error(t('bundles.fillRequired'));
        return;
      }
      body.fixed_price = fp;
    } else {
      const discount = parseFloat(form.discount_percent);
      if (isNaN(discount) || discount < 1 || discount > 99) {
        toast.error(t('bundles.fillRequired'));
        return;
      }
      body.discount_percent = discount;
    }
    setSaving(true);
    try {
      const url = editing
        ? API + '/admin/bundles/' + editing.bundle_id
        : API + '/admin/bundles';
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(editing ? t('bundles.bundleUpdated') : t('bundles.bundleCreated'));
        setDialogOpen(false);
        fetchData();
      } else {
        toast.error(t('common.error'));
      }
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async function(bundleId) {
    if (!window.confirm(t('bundles.confirmDelete'))) return;
    try {
      const res = await fetch(API + '/admin/bundles/' + bundleId, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(t('bundles.bundleDeleted'));
        fetchData();
      }
    } catch (e) {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="admin-bundles">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('bundles.manageBundles')}</h1>
          <p className="text-muted-foreground">{t('bundles.manageBundlesDesc')}</p>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="add-bundle-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('bundles.addBundle')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="text-center py-24">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">{t('bundles.noBundles')}</h3>
          <p className="text-muted-foreground mb-6">{t('bundles.noBundlesDesc')}</p>
          <Button onClick={openCreate} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            {t('bundles.addBundle')}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {bundles.map(function(bundle) {
            return (
              <Card key={bundle.bundle_id} className="bg-card border-border" data-testid={'bundle-' + bundle.bundle_id}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/5 rounded-sm flex items-center justify-center">
                        <Package className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{bundle.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {bundle.class_name ? bundle.class_name : t('bundles.allClasses')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {bundle.fixed_price != null && bundle.fixed_price > 0 ? (
                          <div className="flex items-center gap-1 justify-end">
                            <DollarSign className="w-4 h-4 text-primary" />
                            <span className="font-mono text-xl font-bold text-primary">CHF {bundle.fixed_price.toFixed(2)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 justify-end">
                            <Percent className="w-4 h-4 text-green-500" />
                            <span className="font-mono text-xl font-bold text-green-500">{bundle.discount_percent}%</span>
                          </div>
                        )}
                        <span className={'text-xs font-mono uppercase ' + (bundle.is_active ? 'text-green-500' : 'text-muted-foreground')}>
                          {bundle.is_active ? t('prints.active') : t('prints.inactive')}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={function() { openEdit(bundle); }} data-testid={'edit-bundle-' + bundle.bundle_id}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={function() { handleDelete(bundle.bundle_id); }} className="hover:text-destructive" data-testid={'delete-bundle-' + bundle.bundle_id}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <BundleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        pricingMode={pricingMode}
        setPricingMode={setPricingMode}
        classes={classes}
        saving={saving}
        onSave={handleSave}
      />
    </div>
  );
}
